/**
 * Pure transforms for the curation products listing
 * (`GET /curation/runs/{curationRefId}/products`) and detail
 * (`GET /curation/products/{productId}`). Mirrors the pattern in
 * `catalog-operations.mapper.ts` — no Ky calls, no React hooks.
 */

import { toSourceShippingAttributes } from '~/utils/source-shipping';

import type {
  CurationFilterDefinitionWire,
  CurationRunFilterDefinitionWire,
  CurationProductDetailWire,
  CurationProductListResponse,
  CurationProductWire,
  CurationStatsBucketWire,
  CurationStatsWire,
  CurationInternalVersionEntryWire,
  CurationInternalVersionsResponse,
  CurationVersionDetailResponse,
  CurationVersionEntryWire,
  CurationVersionTimelineResponse,
  ProductDetailAttributeWire,
} from '../types/catalog-operations.api.types';
import type {
  CurationFilterDefinition,
  CurationStats,
  CurationStatsBucket,
  Product,
  ProductDetail,
  ProductDetailAttribute,
  ProductsResult,
  ProductVersionEntry,
  ProductVersionSnapshot,
  ProductVersionTimeline,
} from '../types/catalog-operations.types';

/** Normalises the `additional_image_urls` field. The current backend emits
 *  a real `string[]`; older payloads sent a stringified JSON blob. Both
 *  shapes are coerced to a clean array, with malformed JSON falling back to
 *  `[]` instead of throwing — first-render robustness over strictness. */
function parseAdditionalImages(raw: string[] | string | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string');
    }
    return [];
  } catch {
    return [];
  }
}

/** Maps one curation row to the existing `Product` UI type. The payload
 *  doesn't expose tags or trends yet — those columns render empty until the
 *  backend adds them. `batchId` is threaded through so the product card can
 *  build its detail link. */
export function toCurationProduct(wire: CurationProductWire, batchId: string): Product {
  // Prefer the AI-optimized image, then the raw feed image, then any additional
  // image — so the card/table thumbnails match the detail view (which already
  // prioritises `optimized_images`).
  const optimizedImages = (wire.optimized_images ?? []).filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const primaryImage = wire.image_url ?? undefined;
  const additionalImages = parseAdditionalImages(wire.additional_image_urls);
  const imageUrl = optimizedImages[0] ?? primaryImage ?? additionalImages[0];
  // Distinct images across all three sources — drives the tile's count badge, so
  // the same URL appearing in `optimized_images` and `additional_image_urls`
  // must not be counted twice.
  const imageCount = new Set([
    ...optimizedImages,
    ...(primaryImage ? [primaryImage] : []),
    ...additionalImages,
  ]).size;
  /** Trims to undefined so an empty string reads as absent, not as a value.
   *  The explicit `=== ''` is deliberate — `??` alone would let a blank through. */
  const text = (value: string | null): string | undefined => {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? undefined : trimmed;
  };
  const sizes = (wire.sizes ?? []).map((size) => size.trim()).filter(Boolean);

  return {
    id: wire.feed_product_id,
    batchId,
    title: wire.title ?? wire.product?.title ?? '—',
    sourceProductId: wire.source_product_id ?? '—',
    ...(imageUrl ? { imageUrl } : {}),
    tags: [],
    trends: [],
    sku: wire.product?.sku ?? '—',
    brand: wire.brand ?? wire.product?.brand ?? '—',
    category: wire.google_product_category ?? '—',
    ...(text(wire.gender) ? { gender: text(wire.gender) } : {}),
    ...(text(wire.age_group) ? { ageGroup: text(wire.age_group) } : {}),
    ...(sizes.length > 0 ? { sizes } : {}),
    ...(text(wire.color) ? { color: text(wire.color) } : {}),
    ...(text(wire.material) ? { material: text(wire.material) } : {}),
    ...(text(wire.pattern) ? { pattern: text(wire.pattern) } : {}),
    ...(wire.completeness_score != null ? { completenessScore: wire.completeness_score } : {}),
    ...(wire.enrichment_score != null ? { enrichmentScore: wire.enrichment_score } : {}),
    ...(wire.pre_enrichment_score != null ? { preEnrichmentScore: wire.pre_enrichment_score } : {}),
    ...(text(wire.price_amount) ? { price: text(wire.price_amount) } : {}),
    ...(text(wire.price_currency) ? { currency: text(wire.price_currency) } : {}),
    imageCount,
    // Spread conditionally so an absent field stays absent rather than becoming
    // `undefined` — `exactOptionalPropertyTypes` forbids the latter, and more to
    // the point "no version data" and "version 0" must not converge.
    ...(wire.version != null ? { version: wire.version } : {}),
    ...(wire.feed_product_version != null ? { feedProductVersion: wire.feed_product_version } : {}),
    ...(wire.product_version != null ? { productVersion: wire.product_version } : {}),
    isNew: wire.is_new,
    ...(text(wire.needs_attention_label_description)
      ? { needsAttentionReason: text(wire.needs_attention_label_description) }
      : {}),
  };
}

/** Maps the full response into the UI-friendly `ProductsResult` shape that
 *  the existing `<ProductsListView>` already consumes. The optional `counts`
 *  block is forwarded so the products page can drive its tab badges off the
 *  same response (and watch counts shift as filters change). */
export function toCurationProductsResult(
  wire: CurationProductListResponse,
  batchId: string,
): ProductsResult {
  return {
    products: wire.data.map((row) => toCurationProduct(row, batchId)),
    total: wire.pagination.total,
    ...(wire.counts
      ? {
          counts: {
            total: wire.counts.total,
            newlyEnriched: wire.counts.new,
            needsAttention: wire.counts.needs_attention,
          },
        }
      : {}),
  };
}

/** Translates the curation `feed_source` enum into the platform display name
 *  the UI uses for logos / labels. Falls back to an em dash when unknown. */
function feedSourceToPlatform(feedSource: string | null): string {
  if (!feedSource) return '—';
  const normalized = feedSource.toUpperCase();
  if (normalized.includes('GOOGLE') || normalized === 'GMC') return 'Google';
  if (normalized.includes('META') || normalized.includes('FACEBOOK')) return 'Meta';
  return feedSource;
}

/** Derives the UI status badge from the curation `needs_attention` flag —
 *  the only signal currently exposed on the detail payload. */
function curationStatusFor(needsAttention: boolean): ProductDetail['status'] {
  return needsAttention ? 'needs-attention' : 'confirmed';
}

/** Coerces an arbitrary curation field value into a display-ready string.
 *  Returns `undefined` for null/empty so callers can drop empty entries from
 *  the `rawFields` map instead of rendering blanks. Arrays join with `, `;
 *  plain objects flatten to `key: value, key: value` for readable display. */
function stringifyRawValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => stringifyRawValue(item))
      .filter((v): v is string => Boolean(v));
    return parts.length > 0 ? parts.join(', ') : undefined;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => {
        const sv = stringifyRawValue(v);
        return sv ? `${k}: ${sv}` : null;
      })
      .filter((v): v is string => Boolean(v));
    return entries.length > 0 ? entries.join(', ') : undefined;
  }
  return undefined;
}

/** Converts the curation `product_details` array (new object shape) to the
 *  normalised camelCase format. Returns `undefined` for the legacy
 *  string/string[] shapes or empty arrays. */
function parseProductDetailsStructured(
  raw: CurationProductDetailWire['product_details'],
): ProductDetailAttribute[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const first = raw[0];
  if (first === undefined || typeof first !== 'object' || !('attribute_name' in first))
    return undefined;
  return (raw as ProductDetailAttributeWire[]).map((item) => ({
    sectionName: item.section_name,
    attributeName: item.attribute_name,
    attributeValue: item.attribute_value,
  }));
}

/** Picks the curation fields that surface as "Current" values in the
 *  optimization accordion and normalises them to display strings. Empty/null
 *  entries are dropped so the page can render `-` when a key is missing. */
function buildRawFields(wire: CurationProductDetailWire): Record<string, string> {
  const candidates: Record<string, unknown> = {
    title: wire.title,
    description: wire.description,
    brand: wire.brand,
    color: wire.color,
    gender: wire.gender,
    material: wire.material,
    pattern: wire.pattern,
    age_group: wire.age_group,
    sizes: wire.sizes,
    product_types: wire.product_types,
    product_highlights: wire.product_highlights,
    custom_labels: wire.custom_labels,
    extra_attributes: wire.extra_attributes,
  };
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(candidates)) {
    const stringified = stringifyRawValue(value);
    if (stringified) out[key] = stringified;
  }
  return out;
}

/** Maps the curation product detail wire into the existing `ProductDetail`
 *  UI type. Fields not yet exposed by the curation API (counts, tags, trends)
 *  are stubbed so the page can render the overview half today; enrichment
 *  score + optimizations are intentionally omitted (separate API). */
export function toCurationProductDetail(wire: CurationProductDetailWire): ProductDetail {
  const optimizedImages = (wire.optimized_images ?? []).filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const primaryImage = wire.image_url ?? undefined;
  const additionalImages = parseAdditionalImages(wire.additional_image_urls);
  const fallbackImages = [primaryImage, ...additionalImages].filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const images = optimizedImages.length > 0 ? optimizedImages : fallbackImages;
  const platform = feedSourceToPlatform(wire.feed_source);
  const deliveryDate = wire.last_seen_at ?? wire.updated_at ?? wire.created_at ?? '';
  const productDetailsStructured = parseProductDetailsStructured(wire.product_details);
  const sourceShipping = toSourceShippingAttributes(wire, 'snake');
  return {
    id: wire.feed_product_id,
    title: wire.title ?? wire.product?.title ?? '—',
    retailerProductId: wire.source_product_id ?? '—',
    images,
    lilyId: wire.feed_product_id || (wire.product?.id ?? '—'),
    // Strictly the nested product id — no feed-id fallback. See the field's doc.
    ...(wire.product?.id ? { internalProductId: wire.product.id } : {}),
    sku: wire.variant_sku ?? wire.product?.sku ?? wire.source_product_id ?? '—',
    brand: wire.brand ?? wire.product?.brand ?? '—',
    retailerCategory: wire.google_product_category ?? '—',
    tags: [],
    trends: [],
    deliveryDate,
    attributeCount: 0,
    trendCount: 0,
    highlightCount: 0,
    searchDescriptorCount: 0,
    sourcePlatform: platform,
    destinationPlatform: platform,
    status: curationStatusFor(wire.needs_attention),
    rawFields: buildRawFields(wire),
    feedSource: wire.feed_source,
    ...(productDetailsStructured ? { productDetailsStructured } : {}),
    ...(sourceShipping ? { sourceShipping } : {}),
    // Conditional spreads: absent stays absent. Version data only exists once
    // curation ships versioning, and "unknown" must not become "version 0".
    ...(wire.version != null ? { version: wire.version } : {}),
    ...(wire.total_versions != null ? { totalVersions: wire.total_versions } : {}),
  };
}

/** Renames the snake_case bucket fields to the camelCase UI shape. */
function toStatsBucket(wire: CurationStatsBucketWire): CurationStatsBucket {
  return {
    total: wire.total,
    valid: wire.valid,
    needsAttention: wire.needs_attention,
  };
}

/** Maps the curation stats payload into the UI type. `perSource` keys are
 *  passed through verbatim (connector codes) so the UI can render
 *  per-connector breakdowns when needed. */
export function toCurationStats(wire: CurationStatsWire): CurationStats {
  const perSource: Record<string, CurationStatsBucket> = {};
  for (const [key, bucket] of Object.entries(wire.per_source)) {
    perSource[key] = toStatsBucket(bucket);
  }
  return {
    subsidiaryId: wire.subsidiary_id,
    connectorCode: wire.connector_code,
    total: wire.total,
    valid: wire.valid,
    needsAttention: wire.needs_attention,
    perSource,
  };
}

/** Maps one wire filter to the UI shape. Drops `multi_select` filters with
 *  no options (backend returned an empty list — nothing to render). For
 *  non-multi-select types `options` is null on the wire and we coerce to []
 *  so the consumer can keep a single field shape. */
export function toCurationFilterDefinition(
  wire: CurationFilterDefinitionWire,
): CurationFilterDefinition {
  return {
    key: wire.key,
    label: wire.label,
    type: wire.type,
    options: wire.options ?? [],
  };
}

/** Filters the wire list down to entries the UI actually renders today.
 *  `needs_attention` is driven by the tab on the per-batch listing, and
 *  `state` is implied by the tab too — both are hidden from the drawer.
 *  Empty `multi_select` filters are also dropped (no options to choose). */
export function toCurationFilterDefinitions(
  wire: CurationFilterDefinitionWire[],
): CurationFilterDefinition[] {
  const HIDDEN_KEYS = new Set(['needs_attention', 'state']);
  return wire
    .filter((f) => !HIDDEN_KEYS.has(f.key))
    .filter((f) => f.type !== 'multi_select' || (f.options?.length ?? 0) > 0)
    .map(toCurationFilterDefinition);
}

/** Suffix the run-scoped listing expects on a substring filter — a bare
 *  `attr.title=x` is an exact (IN) match there, unlike the legacy `title` key. */
const CONTAINS_SUFFIX = '__contains';

/** Maps one run-scoped wire filter to the UI shape. A `text_contains` key gets
 *  its `__contains` suffix here, so the drawer's value is already keyed by the
 *  query param the listing reads and the serializer needs no type awareness. */
export function toCurationRunFilterDefinition(
  wire: CurationRunFilterDefinitionWire,
): CurationFilterDefinition {
  const isNamespaced = wire.key.startsWith('attr.') || wire.key.startsWith('extra.');
  return {
    key: wire.type === 'text_contains' && isNamespaced ? `${wire.key}${CONTAINS_SUFFIX}` : wire.key,
    label: wire.label,
    type: wire.type,
    options: wire.options ?? [],
    ...(wire.option_counts ? { optionCounts: wire.option_counts } : {}),
    ...(wire.options_truncated ? { optionsTruncated: true } : {}),
    ...(wire.type === 'numeric_range' || wire.type === 'date_range'
      ? { min: wire.min, max: wire.max }
      : {}),
  };
}

/** Run-scoped counterpart of `toCurationFilterDefinitions`. `needs_attention`
 *  is tab-driven and `attr.state` is implied by the tab, so both are hidden;
 *  empty `multi_select` filters are dropped. */
export function toCurationRunFilterDefinitions(
  wire: CurationRunFilterDefinitionWire[],
): CurationFilterDefinition[] {
  const HIDDEN_KEYS = new Set(['needs_attention', 'attr.state']);
  return wire
    .filter((f) => !HIDDEN_KEYS.has(f.key))
    .filter((f) => f.type !== 'multi_select' || (f.options?.length ?? 0) > 0)
    .map(toCurationRunFilterDefinition);
}

/**
 * Maps one version-timeline entry. `changed_fields` is normalised to an array so
 * consumers can render a change log without null checks — the field is computed
 * server-side against the NEXT OLDER version and is legitimately empty on the
 * oldest entry, which has nothing to compare against.
 */
export function toProductVersionEntry(
  wire: CurationVersionEntryWire | CurationInternalVersionEntryWire,
): ProductVersionEntry {
  // `title`/`brand` ride only on the feed timeline; the internal one describes a
  // different entity and omits them. Spread conditionally so the field is absent
  // rather than an explicit `undefined` (exactOptionalPropertyTypes).
  const preview = 'title' in wire ? { title: wire.title, brand: wire.brand } : {};
  return {
    version: wire.version,
    validFrom: wire.valid_from,
    validTo: wire.valid_to,
    isCurrent: wire.is_current,
    createdByRunId: wire.created_by_run_id,
    supersededByRunId: wire.superseded_by_run_id,
    changeSource: wire.change_source,
    changedFields: wire.changed_fields ?? [],
    ...preview,
  };
}

/** Timeline envelope. The response is bare — `versions`, not `data` — and carries
 *  the two counters the header needs. */
export function toProductVersionTimeline(
  wire: CurationVersionTimelineResponse | CurationInternalVersionsResponse,
): ProductVersionTimeline {
  return {
    currentVersion: wire.current_version,
    totalVersions: wire.total_versions,
    entries: wire.versions.map(toProductVersionEntry),
  };
}

/** One archived version. `data` is the version's own column map — not a detail
 *  payload — so it is carried through as-is for a read-only field list. */
export function toProductVersionSnapshot(
  wire: CurationVersionDetailResponse,
): ProductVersionSnapshot {
  return {
    version: wire.version,
    isCurrent: wire.is_current,
    validFrom: wire.valid_from,
    createdByRunId: wire.created_by_run_id,
    fields: wire.data,
  };
}
